import React, { useState, useEffect } from 'react';
import { Coins, Plus, Minus, Search, Edit2, History, Users } from 'lucide-react';
import apiClient from '../../utils/api';
import './CoinsManagement.css';

function CoinsManagement() {
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'packages'
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState({
    credit_type: 'permanent',
    delta: 0,
    reason: ''
  });
  const [userHistory, setUserHistory] = useState([]);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else {
      loadPackages();
    }
  }, [activeTab, searchTerm]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/credits/users?search=${searchTerm}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPackages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/credits/packages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setPackages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading packages:', error);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustCredits = async () => {
    if (!adjustmentData.reason.trim()) {
      alert('Bitte geben Sie einen Grund an!');
      return;
    }

    if (adjustmentData.delta === 0) {
      alert('Bitte geben Sie einen Betrag ein!');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/credits/adjust`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          credit_type: adjustmentData.credit_type,
          delta: parseInt(adjustmentData.delta),
          reason: adjustmentData.reason
        })
      });

      if (response.ok) {
        alert('Credits erfolgreich angepasst!');
        setShowAdjustModal(false);
        setAdjustmentData({ credit_type: 'permanent', delta: 0, reason: '' });
        loadUsers();
      } else {
        const error = await response.json();
        alert(`Fehler: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error adjusting credits:', error);
      alert('Fehler beim Anpassen der Credits!');
    }
  };

  const loadUserHistory = async (userId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/credits/user/${userId}/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setUserHistory(data.transactions || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const openAdjustModal = (user) => {
    setSelectedUser(user);
    setShowAdjustModal(true);
  };

  const openHistoryModal = async (user) => {
    setSelectedUser(user);
    setShowHistoryModal(true);
    await loadUserHistory(user.user_id);
  };

  const getCreditTypeColor = (type) => {
    const colors = {
      daily: '#06b6d4',
      monthly: '#f59e0b',
      permanent: '#c2a45a'
    };
    return colors[type] || '#888';
  };

  return (
    <div className="coins-management">
      <div className="coins-header">
        <div className="coins-header-icon">
          <Coins size={32} />
        </div>
        <div>
          <h2>Coins-Verwaltung</h2>
          <p>Verwalte User-Credits und Coin-Pakete</p>
        </div>
      </div>

      <div className="coins-tabs">
        <button
          className={`coins-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={18} />
          User Credits
        </button>
        <button
          className={`coins-tab ${activeTab === 'packages' ? 'active' : ''}`}
          onClick={() => setActiveTab('packages')}
        >
          <Coins size={18} />
          Coin-Pakete
        </button>
      </div>

      {activeTab === 'users' ? (
        <div className="users-section">
          <div className="search-bar">
            <Search size={18} />
            <input
              type="text"
              placeholder="Suche nach Name oder E-Mail..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="loading">Lade Daten...</div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>E-Mail</th>
                    <th>Abo-Plan</th>
                    <th style={{textAlign: 'center'}}>Täglich</th>
                    <th style={{textAlign: 'center'}}>Monatlich</th>
                    <th style={{textAlign: 'center'}}>Permanent</th>
                    <th style={{textAlign: 'center'}}>Gesamt</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.user_id}>
                      <td>{user.user_name || '-'}</td>
                      <td>{user.user_email}</td>
                      <td>
                        <span className={`plan-badge ${user.subscription_plan}`}>
                          {user.subscription_plan || 'Free'}
                        </span>
                      </td>
                      <td style={{textAlign: 'center', color: getCreditTypeColor('daily')}}>
                        {user.daily_credits}
                      </td>
                      <td style={{textAlign: 'center', color: getCreditTypeColor('monthly')}}>
                        {user.monthly_credits}
                      </td>
                      <td style={{textAlign: 'center', color: getCreditTypeColor('permanent')}}>
                        {user.permanent_credits}
                      </td>
                      <td style={{textAlign: 'center', fontWeight: 'bold', color: '#c2a45a'}}>
                        {user.coins}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn edit"
                            onClick={() => openAdjustModal(user)}
                            title="Credits anpassen"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="action-btn history"
                            onClick={() => openHistoryModal(user)}
                            title="Historie anzeigen"
                          >
                            <History size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <div className="no-data">Keine User gefunden</div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="packages-section">
          <div className="packages-info">
            <p>Coin-Pakete werden automatisch aus der Datenbank geladen. Zum Bearbeiten der Pakete verwenden Sie die API-Endpoints.</p>
          </div>
          <div className="packages-grid-admin">
            {packages.map((pkg) => (
              <div key={pkg._id} className="package-card-admin">
                <div className="package-header-admin">
                  <Coins size={24} />
                  <span className="package-coins-admin">{pkg.coins + pkg.bonus_coins}</span>
                </div>
                {pkg.bonus_coins > 0 && (
                  <div className="package-bonus">+{pkg.bonus_coins} Bonus</div>
                )}
                <div className="package-price-admin">{pkg.price_eur.toFixed(2)}€</div>
                <div className="package-description-admin">{pkg.description}</div>
                <div className="package-status">
                  {pkg.is_active ? (
                    <span className="status-active">✓ Aktiv</span>
                  ) : (
                    <span className="status-inactive">✗ Inaktiv</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Adjust Credits Modal */}
      {showAdjustModal && (
        <div className="modal-overlay" onClick={() => setShowAdjustModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Credits anpassen</h3>
              <button onClick={() => setShowAdjustModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-info-modal">
                <p><strong>User:</strong> {selectedUser?.user_name || selectedUser?.user_email}</p>
                <p><strong>Aktuell:</strong> {selectedUser?.coins} Coins</p>
              </div>

              <div className="form-group">
                <label>Credit-Typ:</label>
                <select
                  value={adjustmentData.credit_type}
                  onChange={(e) => setAdjustmentData({...adjustmentData, credit_type: e.target.value})}
                >
                  <option value="daily">Daily Credits</option>
                  <option value="monthly">Monthly Credits</option>
                  <option value="permanent">Permanent Credits</option>
                </select>
              </div>

              <div className="form-group">
                <label>Betrag (+ hinzufügen / - abziehen):</label>
                <input
                  type="number"
                  value={adjustmentData.delta}
                  onChange={(e) => setAdjustmentData({...adjustmentData, delta: e.target.value})}
                  placeholder="z.B. +100 oder -50"
                />
              </div>

              <div className="form-group">
                <label>Grund (Pflichtfeld):</label>
                <textarea
                  value={adjustmentData.reason}
                  onChange={(e) => setAdjustmentData({...adjustmentData, reason: e.target.value})}
                  placeholder="z.B. Bonus für Treue, Korrektur wegen Fehler..."
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setShowAdjustModal(false)}>
                  Abbrechen
                </button>
                <button className="btn-save" onClick={handleAdjustCredits}>
                  Speichern
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Credit-Historie</h3>
              <button onClick={() => setShowHistoryModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-info-modal">
                <p><strong>User:</strong> {selectedUser?.user_name || selectedUser?.user_email}</p>
              </div>

              <div className="history-table-container">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Typ</th>
                      <th>Credit-Art</th>
                      <th>Betrag</th>
                      <th>Balance</th>
                      <th>Beschreibung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userHistory.map((tx) => (
                      <tr key={tx._id}>
                        <td>{new Date(tx.created_at).toLocaleString('de-DE')}</td>
                        <td>
                          <span className={`tx-type ${tx.transaction_type}`}>
                            {tx.transaction_type}
                          </span>
                        </td>
                        <td>
                          <span style={{color: getCreditTypeColor(tx.credit_type)}}>
                            {tx.credit_type}
                          </span>
                        </td>
                        <td className={tx.amount >= 0 ? 'positive' : 'negative'}>
                          {tx.amount > 0 ? '+' : ''}{tx.amount}
                        </td>
                        <td>{tx.balance_after}</td>
                        <td>{tx.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {userHistory.length === 0 && (
                  <div className="no-data">Keine Transaktionen gefunden</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoinsManagement;
